import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useAuth } from '@clerk/clerk-react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';
import { FiAlertTriangle, FiInfo, FiRefreshCw } from 'react-icons/fi';
import { apiJson } from '@/lib/api';
import type {
  AdminCustomerLifetime,
  AdminLtvDailyPoint,
  AdminLtvMetricsResponse,
  AdminMetricsRange,
} from '@/lib/types';

const RANGE_OPTIONS: { range: AdminMetricsRange; label: string; description: string }[] = [
  { range: '24h', label: '24h', description: 'Today (Pacific)' },
  { range: '7d', label: '7d', description: 'Last 7 days' },
  { range: '30d', label: '30d', description: 'Last 30 days' },
  { range: '60d', label: '60d', description: 'Last 60 days' },
  { range: '90d', label: '90d', description: 'Last 90 days' },
];

const LIFETIME_OPTIONS: {
  lifetime: AdminCustomerLifetime;
  label: string;
  description: string;
}[] = [
  {
    lifetime: 'unbounded',
    label: 'Unbounded',
    description: 'All revenue for each cohort',
  },
  {
    lifetime: '30_days',
    label: '30 days',
    description: 'Revenue in first 30 days after first seen',
  },
  {
    lifetime: '7_days',
    label: '7 days',
    description: 'Revenue in first 7 days after first seen',
  },
];

const ALLOWED_RANGES = new Set<string>(RANGE_OPTIONS.map((r) => r.range));
const ALLOWED_LIFETIMES = new Set<string>(LIFETIME_OPTIONS.map((l) => l.lifetime));

const OVERVIEW_LABELS: Record<string, string> = {
  mrr: 'MRR',
  arr: 'ARR',
  active_subscriptions: 'Active subscriptions',
  active_trials: 'Active trials',
  revenue: 'Revenue (30d)',
};

function formatDateLabel(ymd: string): string {
  const [y, m, d] = ymd.split('-').map(Number);
  if (!y || !m || !d) return ymd;
  const dt = new Date(Date.UTC(y, m - 1, d));
  return dt.toLocaleDateString(undefined, {
    timeZone: 'UTC',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  const dt = new Date(iso);
  if (Number.isNaN(dt.getTime())) return iso;
  return dt.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function formatCurrency(value: number | null | undefined, currency: string | null): string {
  if (value == null || Number.isNaN(value)) return '—';
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: currency ?? 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  } catch {
    return `$${value.toFixed(2)}`;
  }
}

function formatAxisDate(ymd: string, showYear: boolean): string {
  const [y, m, d] = ymd.split('-').map(Number);
  if (!y || !m || !d) return ymd;
  const dt = new Date(Date.UTC(y, m - 1, d));
  return dt.toLocaleDateString(undefined, {
    timeZone: 'UTC',
    month: 'short',
    day: 'numeric',
    ...(showYear ? { year: '2-digit' } : {}),
  });
}

function niceAxisMax(value: number): number {
  if (value <= 0) return 5;
  if (value <= 5) return 5;
  const magnitude = 10 ** Math.floor(Math.log10(value));
  const normalized = value / magnitude;
  const nice =
    normalized <= 1 ? 1 : normalized <= 2 ? 2 : normalized <= 5 ? 5 : 10;
  return nice * magnitude;
}

function yAxisTicks(max: number): number[] {
  const top = niceAxisMax(max);
  const step =
    top <= 5 ? 1 : top <= 10 ? 2 : top <= 25 ? 5 : top <= 50 ? 10 : Math.ceil(top / 4 / 10) * 10;
  const ticks: number[] = [];
  for (let v = 0; v <= top; v += step) ticks.push(v);
  if (ticks[ticks.length - 1] !== top) ticks.push(top);
  return ticks;
}

function xLabelIndices(count: number): number[] {
  if (count <= 1) return [0];
  if (count <= 8) return Array.from({ length: count }, (_, i) => i);
  const targetLabels = count <= 31 ? 7 : 10;
  const step = Math.max(1, Math.round((count - 1) / (targetLabels - 1)));
  const indices: number[] = [0];
  for (let i = step; i < count - 1; i += step) indices.push(i);
  if (indices[indices.length - 1] !== count - 1) indices.push(count - 1);
  return indices;
}

function StatTile({
  label,
  value,
  hint,
}: {
  label: string;
  value: string;
  hint?: string;
}) {
  return (
    <div className='rounded-lg border border-gray-200 bg-white p-4 shadow-sm'>
      <p className='text-xs font-semibold uppercase tracking-wide text-gray-500'>
        {label}
      </p>
      <p className='mt-1 text-2xl font-semibold tabular-nums text-gray-900'>
        {value}
      </p>
      {hint ? <p className='mt-1 text-xs text-gray-500'>{hint}</p> : null}
    </div>
  );
}

function LtvLineChart({
  title,
  subtitle,
  daily,
  currency,
  highlightIncomplete = false,
}: {
  title: string;
  subtitle: string;
  daily: AdminLtvDailyPoint[];
  currency: string | null;
  highlightIncomplete?: boolean;
}) {
  const chartWidth = 900;
  const chartHeight = 280;
  const padding = { top: 16, right: 16, bottom: 48, left: 56 };
  const innerW = chartWidth - padding.left - padding.right;
  const innerH = chartHeight - padding.top - padding.bottom;

  const maxValue = Math.max(...daily.map((d) => d.ltv), 0);
  const axisMax = niceAxisMax(maxValue);
  const ticks = yAxisTicks(axisMax);
  const showYear = daily.length > 60;
  const labelIndices = new Set(xLabelIndices(daily.length));

  const points = daily.map((d, i) => {
    const x =
      daily.length <= 1
        ? padding.left + innerW / 2
        : padding.left + (i / (daily.length - 1)) * innerW;
    const y = padding.top + innerH - (d.ltv / axisMax) * innerH;
    return { ...d, x, y };
  });

  const pathD =
    points.length >= 2
      ? points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ')
      : '';

  return (
    <div>
      <div className='mb-4'>
        <h3 className='text-base font-semibold text-gray-900'>{title}</h3>
        <p className='mt-0.5 text-sm text-gray-500'>{subtitle}</p>
      </div>
      <div className='overflow-x-auto'>
        <svg
          viewBox={`0 0 ${chartWidth} ${chartHeight}`}
          className='min-w-[640px] w-full'
          role='img'
          aria-label={title}
        >
          {ticks.map((tick) => {
            const y = padding.top + innerH - (tick / axisMax) * innerH;
            return (
              <g key={tick}>
                <line
                  x1={padding.left}
                  y1={y}
                  x2={padding.left + innerW}
                  y2={y}
                  stroke='#e5e7eb'
                  strokeWidth={1}
                />
                <text
                  x={padding.left - 8}
                  y={y + 4}
                  textAnchor='end'
                  className='fill-gray-400 text-[10px]'
                >
                  {formatCurrency(tick, currency)}
                </text>
              </g>
            );
          })}

          {pathD ? (
            <path
              d={pathD}
              fill='none'
              stroke='#018A9D'
              strokeWidth={2.5}
              strokeLinejoin='round'
              strokeLinecap='round'
            />
          ) : null}

          {points.map((p) => (
            <g key={p.date}>
              <circle
                cx={p.x}
                cy={p.y}
                r={4}
                fill={highlightIncomplete && p.incomplete ? '#f59e0b' : '#018A9D'}
                stroke='white'
                strokeWidth={1.5}
              >
                <title>
                  {formatDateLabel(p.date)}: {formatCurrency(p.ltv, currency)}
                  {p.incomplete ? ' (incomplete cohort)' : ''}
                </title>
              </circle>
            </g>
          ))}

          {daily.map((d, i) =>
            labelIndices.has(i) ? (
              <text
                key={d.date}
                x={points[i]?.x ?? 0}
                y={chartHeight - 12}
                textAnchor='middle'
                className='fill-gray-500 text-[10px]'
              >
                {formatAxisDate(d.date, showYear)}
              </text>
            ) : null,
          )}

          <text
            x={padding.left + innerW / 2}
            y={chartHeight - 2}
            textAnchor='middle'
            className='fill-gray-500 text-[11px] font-medium'
          >
            Cohort date (Pacific)
          </text>
        </svg>
      </div>
      {highlightIncomplete ? (
        <p className='mt-2 text-[11px] text-gray-400'>
          Orange points are incomplete cohorts — LTV may still increase as those customers mature.
        </p>
      ) : null}
    </div>
  );
}

function LtvBarChart({
  title,
  subtitle,
  daily,
  currency,
  barColor,
}: {
  title: string;
  subtitle: string;
  daily: AdminLtvDailyPoint[];
  currency: string | null;
  barColor: string;
}) {
  const chartWidth = 900;
  const chartHeight = 280;
  const padding = { top: 16, right: 16, bottom: 48, left: 56 };
  const innerW = chartWidth - padding.left - padding.right;
  const innerH = chartHeight - padding.top - padding.bottom;

  const maxValue = Math.max(...daily.map((d) => d.ltv), 0);
  const axisMax = niceAxisMax(maxValue);
  const ticks = yAxisTicks(axisMax);
  const showYear = daily.length > 60;
  const labelIndices = new Set(xLabelIndices(daily.length));
  const barGap = 2;
  const barWidth = Math.max(2, innerW / Math.max(daily.length, 1) - barGap);

  return (
    <div>
      <div className='mb-4'>
        <h3 className='text-base font-semibold text-gray-900'>{title}</h3>
        <p className='mt-0.5 text-sm text-gray-500'>{subtitle}</p>
      </div>
      <div className='overflow-x-auto'>
        <svg
          viewBox={`0 0 ${chartWidth} ${chartHeight}`}
          className='min-w-[640px] w-full'
          role='img'
          aria-label={title}
        >
          {ticks.map((tick) => {
            const y = padding.top + innerH - (tick / axisMax) * innerH;
            return (
              <g key={tick}>
                <line
                  x1={padding.left}
                  y1={y}
                  x2={padding.left + innerW}
                  y2={y}
                  stroke='#e5e7eb'
                  strokeWidth={1}
                />
                <text
                  x={padding.left - 8}
                  y={y + 4}
                  textAnchor='end'
                  className='fill-gray-400 text-[10px]'
                >
                  {formatCurrency(tick, currency)}
                </text>
              </g>
            );
          })}

          {daily.map((d, i) => {
            const barH = axisMax > 0 ? (d.ltv / axisMax) * innerH : 0;
            const x = padding.left + i * (barWidth + barGap);
            const y = padding.top + innerH - barH;
            return (
              <rect
                key={d.date}
                x={x}
                y={y}
                width={barWidth}
                height={barH}
                fill={barColor}
                rx={2}
              >
                <title>{`${formatDateLabel(d.date)}: ${formatCurrency(d.ltv, currency)}`}</title>
              </rect>
            );
          })}

          {daily.map((d, i) =>
            labelIndices.has(i) ? (
              <text
                key={`label-${d.date}`}
                x={padding.left + i * (barWidth + barGap) + barWidth / 2}
                y={chartHeight - 12}
                textAnchor='middle'
                className='fill-gray-500 text-[10px]'
              >
                {formatAxisDate(d.date, showYear)}
              </text>
            ) : null,
          )}
        </svg>
      </div>
    </div>
  );
}

function LtvSourceBanner({ data }: { data: AdminLtvMetricsResponse }) {
  const paying = data.ltv_per_paying_customer;
  if (!data.env_configured) {
    return (
      <div className='rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-950 flex gap-2'>
        <FiAlertTriangle className='mt-0.5 h-4 w-4 shrink-0' aria-hidden />
        <div className='min-w-0 space-y-1'>
          <p className='font-semibold'>RevenueCat Charts API is not configured</p>
          <p className='text-xs'>
            Set <span className='font-mono'>REVENUECAT_CHARTS_API_KEY</span> (v2 secret with
            charts read) and <span className='font-mono'>REVENUECAT_PROJECT_ID</span> on the API
            deployment.
          </p>
        </div>
      </div>
    );
  }

  if (!paying.ok) {
    return (
      <div className='rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-950 flex gap-2'>
        <FiAlertTriangle className='mt-0.5 h-4 w-4 shrink-0' aria-hidden />
        <div className='min-w-0 space-y-1'>
          <p className='font-semibold'>LTV data unavailable</p>
          <p className='text-xs'>{paying.error ?? 'Could not load LTV from RevenueCat.'}</p>
        </div>
      </div>
    );
  }

  if (paying.cache_status === 'stale') {
    return (
      <div className='rounded-lg border border-amber-200 bg-amber-50/80 px-4 py-2.5 text-xs text-amber-950 flex gap-2'>
        <FiAlertTriangle className='mt-0.5 h-3.5 w-3.5 shrink-0' aria-hidden />
        <p>
          <span className='font-semibold'>Cached LTV data</span>
          {paying.error ? ` — ${paying.error}` : ''}
          {paying.cached_at ? ` · cached ${formatDateTime(paying.cached_at)}` : ''}
        </p>
      </div>
    );
  }

  return (
    <p className='text-[11px] text-gray-400'>
      RevenueCat {paying.chart_display_name ?? 'LTV'} · {paying.customer_lifetime} lifetime
      {paying.revenuecat_last_computed_at
        ? ` · RC computed ${formatDateTime(paying.revenuecat_last_computed_at)}`
        : ''}
      {paying.cached_at && paying.cache_status !== 'bypass'
        ? ` · cached ${formatDateTime(paying.cached_at)} (${paying.cache_status})`
        : paying.cache_status === 'bypass'
          ? ' · live fetch'
          : ''}
    </p>
  );
}

function AdminLtvPage() {
  const { getToken } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { range: rangeParam, lifetime: lifetimeParam } = Route.useSearch();

  const range: AdminMetricsRange = ALLOWED_RANGES.has(rangeParam ?? '')
    ? (rangeParam as AdminMetricsRange)
    : '90d';
  const lifetime: AdminCustomerLifetime = ALLOWED_LIFETIMES.has(lifetimeParam ?? '')
    ? (lifetimeParam as AdminCustomerLifetime)
    : 'unbounded';

  const activeRangeOption =
    RANGE_OPTIONS.find((opt) => opt.range === range) ?? RANGE_OPTIONS[4];
  const activeLifetimeOption =
    LIFETIME_OPTIONS.find((opt) => opt.lifetime === lifetime) ?? LIFETIME_OPTIONS[0];

  const ltvQuery = useQuery({
    queryKey: ['admin', 'ltv', range, lifetime],
    queryFn: () =>
      apiJson<AdminLtvMetricsResponse>(
        getToken,
        `/v1/admin/metrics/ltv?range=${encodeURIComponent(range)}&customer_lifetime=${encodeURIComponent(lifetime)}`,
      ),
  });

  const data = ltvQuery.data;
  const currency = data?.currency ?? 'USD';

  const overviewTiles = useMemo(() => {
    if (!data?.overview?.length) return [];
    const priority = ['mrr', 'arr', 'active_subscriptions', 'active_trials'];
    const byId = new Map(data.overview.map((item) => [item.id, item]));
    const ordered = priority
      .map((id) => byId.get(id))
      .filter((item): item is NonNullable<typeof item> => Boolean(item));
    const rest = data.overview.filter((item) => !priority.includes(item.id));
    return [...ordered, ...rest].slice(0, 4);
  }, [data]);

  const latestMrr = data?.mrr_daily[data.mrr_daily.length - 1]?.ltv;
  const latestRevenue = data?.revenue_daily[data.revenue_daily.length - 1]?.ltv;

  const setRange = (next: AdminMetricsRange) => {
    navigate({ to: '/admin/ltv', search: { range: next, lifetime } });
  };

  const setLifetime = (next: AdminCustomerLifetime) => {
    navigate({ to: '/admin/ltv', search: { range, lifetime: next } });
  };

  const handleRefresh = () => {
    void queryClient.fetchQuery({
      queryKey: ['admin', 'ltv', range, lifetime],
      queryFn: () =>
        apiJson<AdminLtvMetricsResponse>(
          getToken,
          `/v1/admin/metrics/ltv?range=${encodeURIComponent(range)}&customer_lifetime=${encodeURIComponent(lifetime)}&refresh=true`,
        ),
    });
  };

  return (
    <div className='space-y-6'>
      <div className='flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between'>
        <div className='min-w-0'>
          <h1 className='text-xl font-semibold text-gray-900 sm:text-2xl'>LTV</h1>
          <p className='mt-1 text-sm text-gray-500'>
            Lifetime value and revenue metrics from RevenueCat — realized LTV per paying customer
            (ARPPU), per customer (ARPU), MRR, and revenue.
          </p>
        </div>
        <div className='flex flex-wrap items-center gap-2'>
          <div
            className='inline-flex flex-col gap-1.5'
            role='group'
            aria-label='Customer lifetime'
          >
            <span className='text-[11px] font-semibold uppercase tracking-wide text-gray-500 px-0.5'>
              Customer lifetime
            </span>
            <div className='inline-flex rounded-lg border border-gray-300 bg-white p-1 shadow-sm'>
              {LIFETIME_OPTIONS.map((opt) => {
                const active = lifetime === opt.lifetime;
                return (
                  <button
                    key={opt.lifetime}
                    type='button'
                    aria-pressed={active}
                    title={opt.description}
                    onClick={() => setLifetime(opt.lifetime)}
                    className={`rounded-md px-3 py-2 text-xs font-bold transition-all ${
                      active
                        ? 'bg-primary text-white shadow-sm ring-2 ring-primary/30'
                        : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                    }`}
                  >
                    {opt.label}
                  </button>
                );
              })}
            </div>
          </div>
          <div
            className='inline-flex flex-col gap-1.5'
            role='group'
            aria-label='Time range'
          >
            <span className='text-[11px] font-semibold uppercase tracking-wide text-gray-500 px-0.5'>
              Cohort range
            </span>
            <div className='inline-flex rounded-lg border border-gray-300 bg-white p-1 shadow-sm'>
              {RANGE_OPTIONS.map((opt) => {
                const active = range === opt.range;
                return (
                  <button
                    key={opt.range}
                    type='button'
                    aria-pressed={active}
                    title={opt.description}
                    onClick={() => setRange(opt.range)}
                    className={`min-w-[3rem] rounded-md px-3 py-2 text-xs font-bold transition-all ${
                      active
                        ? 'bg-primary text-white shadow-sm ring-2 ring-primary/30'
                        : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                    }`}
                  >
                    {opt.label}
                  </button>
                );
              })}
            </div>
          </div>
          <button
            type='button'
            onClick={handleRefresh}
            disabled={ltvQuery.isFetching}
            className='inline-flex items-center gap-1.5 self-end rounded-md border border-gray-300 bg-white px-3 py-2 text-xs font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-60'
          >
            <FiRefreshCw
              className={`h-3.5 w-3.5 ${ltvQuery.isFetching ? 'animate-spin' : ''}`}
            />
            Refresh
          </button>
        </div>
      </div>

      {ltvQuery.isLoading ? (
        <div className='flex justify-center py-12'>
          <div className='h-10 w-10 animate-spin rounded-full border-2 border-primary border-t-transparent' />
        </div>
      ) : ltvQuery.isError || !data ? (
        <p className='text-red-600'>Could not load LTV metrics.</p>
      ) : (
        <>
          <div className='flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-500'>
            <span className='inline-flex items-center rounded-full bg-primary/10 px-2.5 py-1 text-xs font-semibold text-primary'>
              {activeLifetimeOption.label} · {activeLifetimeOption.description}
            </span>
            <span className='inline-flex items-center rounded-full bg-gray-100 px-2.5 py-1 text-xs font-semibold text-gray-700'>
              {activeRangeOption.label} cohorts
            </span>
            <span className='inline-flex items-center gap-1.5'>
              <FiInfo className='h-3.5 w-3.5 shrink-0' aria-hidden />
              {data.start_date} → {data.end_date} ({data.timezone})
            </span>
          </div>

          <LtvSourceBanner data={data} />

          {overviewTiles.length ? (
            <div className='grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4'>
              {overviewTiles.map((item) => (
                <StatTile
                  key={item.id}
                  label={OVERVIEW_LABELS[item.id] ?? item.id.replace(/_/g, ' ')}
                  value={formatCurrency(item.value, currency)}
                  hint='RevenueCat overview snapshot'
                />
              ))}
            </div>
          ) : null}

          {data.ltv_per_paying_customer.ok ? (
            <>
              <div>
                <h2 className='text-lg font-semibold text-gray-900'>
                  LTV per paying customer (ARPPU)
                </h2>
                <p className='mt-1 text-sm text-gray-500'>
                  Realized revenue per paying customer cohort, minus refunds. Latest complete
                  cohort is the most recent mature period.
                </p>
              </div>

              <div className='grid grid-cols-1 gap-3 sm:grid-cols-3'>
                <StatTile
                  label='Latest complete cohort'
                  value={formatCurrency(
                    data.ltv_per_paying_customer.latest_complete,
                    currency,
                  )}
                  hint='Most recent mature cohort — best for comparisons'
                />
                <StatTile
                  label='Average (complete cohorts)'
                  value={formatCurrency(
                    data.ltv_per_paying_customer.average_complete,
                    currency,
                  )}
                  hint={`Across ${data.start_date} → ${data.end_date}`}
                />
                <StatTile
                  label='Latest cohort (may be incomplete)'
                  value={formatCurrency(data.ltv_per_paying_customer.latest, currency)}
                  hint='Includes immature cohorts when lifetime is unbounded'
                />
              </div>

              <section className='rounded-lg border border-gray-200 bg-white p-4 shadow-sm sm:p-6'>
                <LtvLineChart
                  title='LTV per paying customer by cohort'
                  subtitle={`${activeLifetimeOption.label} customer lifetime · cohorted by first seen date`}
                  daily={data.ltv_per_paying_customer.daily}
                  currency={currency}
                  highlightIncomplete
                />
              </section>
            </>
          ) : null}

          {data.ltv_per_customer.ok ? (
            <>
              <div>
                <h2 className='text-lg font-semibold text-gray-900'>
                  LTV per customer (ARPU)
                </h2>
                <p className='mt-1 text-sm text-gray-500'>
                  Realized revenue per new customer cohort — includes non-paying customers.
                </p>
              </div>

              <div className='grid grid-cols-1 gap-3 sm:grid-cols-3'>
                <StatTile
                  label='Latest complete cohort'
                  value={formatCurrency(data.ltv_per_customer.latest_complete, currency)}
                />
                <StatTile
                  label='Average (complete cohorts)'
                  value={formatCurrency(data.ltv_per_customer.average_complete, currency)}
                />
                <StatTile
                  label='Latest cohort (may be incomplete)'
                  value={formatCurrency(data.ltv_per_customer.latest, currency)}
                />
              </div>

              <section className='rounded-lg border border-gray-200 bg-white p-4 shadow-sm sm:p-6'>
                <LtvLineChart
                  title='LTV per customer by cohort'
                  subtitle={`${activeLifetimeOption.label} customer lifetime · all new customers`}
                  daily={data.ltv_per_customer.daily}
                  currency={currency}
                  highlightIncomplete
                />
              </section>
            </>
          ) : null}

          <div className='grid grid-cols-1 gap-6 xl:grid-cols-2'>
            <section className='rounded-lg border border-gray-200 bg-white p-4 shadow-sm sm:p-6'>
              <div className='mb-4 grid grid-cols-1 gap-3 sm:grid-cols-2'>
                <StatTile
                  label='Latest MRR'
                  value={formatCurrency(latestMrr, currency)}
                  hint={
                    data.mrr_daily.length
                      ? formatDateLabel(data.mrr_daily[data.mrr_daily.length - 1].date)
                      : undefined
                  }
                />
                <StatTile
                  label='Latest daily revenue'
                  value={formatCurrency(latestRevenue, currency)}
                  hint={
                    data.revenue_daily.length
                      ? formatDateLabel(data.revenue_daily[data.revenue_daily.length - 1].date)
                      : undefined
                  }
                />
              </div>
              <LtvBarChart
                title='MRR over time'
                subtitle='Monthly recurring revenue by day'
                daily={data.mrr_daily}
                currency={currency}
                barColor='#6366f1'
              />
            </section>

            <section className='rounded-lg border border-gray-200 bg-white p-4 shadow-sm sm:p-6'>
              <LtvBarChart
                title='Revenue over time'
                subtitle='Daily revenue (gross, minus refunds)'
                daily={data.revenue_daily}
                currency={currency}
                barColor='#10b981'
              />
            </section>
          </div>
        </>
      )}
    </div>
  );
}

export const Route = createFileRoute('/admin/ltv')({
  validateSearch: (search: Record<string, unknown>) => ({
    range:
      typeof search.range === 'string' && ALLOWED_RANGES.has(search.range)
        ? search.range
        : '90d',
    lifetime:
      typeof search.lifetime === 'string' && ALLOWED_LIFETIMES.has(search.lifetime)
        ? search.lifetime
        : 'unbounded',
  }),
  component: AdminLtvPage,
});
